import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar, TableSectionV2 } from 'components/UI';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import { MobileActionMenu, DesktopActionMenu } from 'components/layout';
import Images from 'assets/images/imageData';
import localization from 'assets/localization/localization';
import { toast } from 'react-toastify';
import {
  formatDate,
  getRowActions,
  getStatusBadgeClass,
  getStatusLabel,
  getUserRoleIds,
  hasAnyRole,
} from './referralUtils';
import { INITIAL_REFERRAL_FILTERS, PAGE_ACCESS_ROLE_IDS } from './constants';
import SiteLeadReferralWorkflowModal from './SiteLeadReferralWorkflowModal';
import './siteLeadReferral.css';

const cell = (val) => (val != null && val !== '' ? String(val) : '—');
const PAGE_SIZE = 20;

const toApiFilters = (filters) => ({
  ...(filters.status ? { status: filters.status } : {}),
  include_legacy: filters.include_legacy === true,
});

const SiteLeadReferralList = ({
  referralList = {},
  spocOptions = {},
  rejectReasons = [],
  getReferralList,
  getSpocList,
  getRejectReasons,
  getStatusHistory,
  assignSpoc,
  validateLead,
  rejectLead,
  centralValidate,
  setLeadStage,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_REFERRAL_FILTERS);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modal, setModal] = useState(null);
  const roleIds = useMemo(() => getUserRoleIds(), []);

  const listData = referralList.referrals || [];
  const currentPage = referralList.page || 1;
  const pageLimit = referralList.limit || PAGE_SIZE;
  const pageCount = Math.ceil((referralList.count || 0) / pageLimit) || 0;

  const loadList = useCallback(async (page, filters) => {
    setIsLoading(true);
    try {
      await getReferralList(page, toApiFilters(filters));
    } catch (err) {
      toast.error(err?.message || 'Failed to load referrals');
    } finally {
      setIsLoading(false);
    }
  }, [getReferralList]);

  useEffect(() => {
    if (hasAnyRole(roleIds, PAGE_ACCESS_ROLE_IDS)) {
      loadList(1, INITIAL_REFERRAL_FILTERS);
    }
  }, [roleIds, loadList]);

  const filterToggle = () => setShowFilter(!showFilter);

  const applyFilter = async (filters) => {
    setFilterData(filters);
    await loadList(1, filters);
    filterToggle();
  };

  const removeFilter = () => {
    setFilterData(INITIAL_REFERRAL_FILTERS);
    loadList(1, INITIAL_REFERRAL_FILTERS);
  };

  const appliedFilterLabels = useMemo(() => {
    const labels = [];
    if (filterData.status) {
      labels.push(`Status: ${getStatusLabel(filterData.status)}`);
    }
    if (filterData.include_legacy) {
      labels.push('Include legacy: Yes');
    }
    return labels;
  }, [filterData]);

  const headerBtns = () => {
    const actionsMenuItems = [
      {
        label: localization.showFilters,
        onClick: filterToggle,
        icon: Images?.Filter,
      },
    ];

    return (
      <div className="d-flex align-items-center flex-wrap gap-2">
        <MobileActionMenu items={actionsMenuItems} toggleLabel="Actions" />
        <DesktopActionMenu items={actionsMenuItems} toggleLabel="Actions" />
      </div>
    );
  };

  const openModal = (type, row) => {
    setSelectedRow(row);
    setModal(type);
  };

  const closeModal = () => {
    setModal(null);
    setSelectedRow(null);
  };

  const actionHandlers = {
    onAssignSpoc: (row) => openModal('assign', row),
    onValidate: (row) => openModal('validate', row),
    onReject: (row) => openModal('reject', row),
    onCentralValidate: (row) => openModal('central', row),
    onSetStage: (row) => openModal('stage', row),
    onHistory: (row) => openModal('history', row),
  };

  const tableColumns = [
    { key: 'id', label: 'ID', format: (val) => cell(val) },
    {
      key: 'referred_name',
      label: 'Referred Name',
      format: (_, row) => cell(row.metadata?.referred_name),
    },
    {
      key: 'property_name',
      label: 'Property',
      format: (_, row) => {
        const name = row.flow_data?.property_name;
        if (!name) return '—';
        return (
          <button
            type="button"
            className="site-lead-referral-property-link"
            onClick={() => openModal('flow', row)}
          >
            {name}
          </button>
        );
      },
    },
    {
      key: 'property_type',
      label: 'Type',
      format: (_, row) => cell(row.flow_data?.property_type),
    },
    { key: 'referred_phone', label: 'Phone', format: (val) => cell(val) },
    { key: 'referred_email', label: 'Email', format: (val) => cell(val) },
    {
      key: 'referrer',
      label: 'Referrer',
      format: (_, row) =>
        row.referrer_user_id
          ? `${row.referrer_user_id} (${row.referrer_role || '—'})`
          : '—',
    },
    {
      key: 'current_status',
      label: 'Status',
      format: (val) => (
        <span className={`site-lead-referral-badge ${getStatusBadgeClass(val)}`}>
          {getStatusLabel(val)}
        </span>
      ),
    },
    {
      key: 'lead_stage',
      label: 'Stage',
      format: (_, row) => cell(row.flow_data?.lead_stage),
    },
    {
      key: 'region',
      label: 'Region',
      format: (_, row) => cell(row.flow_data?.region),
    },
    {
      key: 'primary_wallet',
      label: 'Primary Wallet',
      format: (_, row) => cell(row.primary_wallet_request_id),
    },
    {
      key: 'final_wallet',
      label: 'Final Wallet',
      format: (_, row) => cell(row.final_wallet_request_id),
    },
    {
      key: 'created_at',
      label: 'Created',
      format: (val) => formatDate(val),
    },
    {
      key: 'actions',
      label: 'Action',
      getActions: (row) => getRowActions(row, roleIds, actionHandlers),
    },
  ];

  if (!hasAnyRole(roleIds, PAGE_ACCESS_ROLE_IDS)) {
    return (
      <Layout title={localization.siteLeadReferral} isGoback useNewHeaderLayout>
        <p className="p-3">You do not have access to this page.</p>
      </Layout>
    );
  }

  const srNo = (currentPage - 1) * pageLimit + 1;

  return (
    <Layout
      title={localization.siteLeadReferral}
      Buttons={headerBtns}
      isGoback
      useNewHeaderLayout
    >
      <AppliedFiltersBar
        items={appliedFilterLabels}
        onClear={removeFilter}
        clearButtonLabel="Remove filter"
        clearButtonDisabled={isLoading}
      />
      <TableSectionV2
        hideHeader
        hideTitleAndStatusFilter
        className="site-lead-referral-table-section"
        tableData={listData}
        tableColumns={tableColumns}
        tableSectionProps={{ srNo }}
        pageCount={pageCount}
        currentPage={currentPage}
        onPageChange={(val) => loadList(val.selected + 1, filterData)}
        isLoading={isLoading}
        emptyMessage="No referrals found."
      />

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={applyFilter}
        siteLeadReferralFilter
        listFilterData={{ appliedFilterValues: filterData }}
      />

      {modal && selectedRow ? (
        <SiteLeadReferralWorkflowModal
          action={modal}
          row={selectedRow}
          rejectReasons={rejectReasons}
          spocOptions={spocOptions}
          incentiveAmounts={referralList.incentiveAmounts}
          getRejectReasons={getRejectReasons}
          getSpocList={getSpocList}
          getStatusHistory={getStatusHistory}
          assignSpoc={assignSpoc}
          validateLead={validateLead}
          rejectLead={rejectLead}
          centralValidate={centralValidate}
          setLeadStage={setLeadStage}
          onHide={closeModal}
          onSuccess={() => loadList(currentPage, filterData)}
        />
      ) : null}
    </Layout>
  );
};

export default SiteLeadReferralList;
